// Clipboard singleton for copy/paste of garden objects, zones, and pins

import type { GardenObject } from '@/types/garden'
import type { Zone, ContextPin } from '@/types/zone'
import { debug } from '@/utils/debug'

export type SingleClipboardItem =
  | { source: 'gardenObject'; data: GardenObject }
  | { source: 'zone'; data: Zone }
  | { source: 'pin'; data: ContextPin }

export type ClipboardEntry =
  | SingleClipboardItem
  | { source: 'multi'; data: SingleClipboardItem[] }

type ClipboardHandler = {
  copy: () => void
  paste: () => void
}

class ClipboardManager {
  private entry: ClipboardEntry | null = null
  private handler: ClipboardHandler | null = null

  copyGardenObject(obj: GardenObject): void {
    this.entry = {
      source: 'gardenObject',
      data: {
        ...obj,
        points: obj.points ? obj.points.map(p => ({ ...p })) : undefined,
      },
    }
    debug.log('Clipboard', `Copied garden object: ${obj.type} ${obj.id}`)
  }

  copyZone(zone: Zone): void {
    this.entry = {
      source: 'zone',
      data: {
        ...zone,
        points: zone.points ? zone.points.map(p => ({ ...p })) : undefined,
      },
    }
    debug.log('Clipboard', `Copied zone: ${zone.zoneType} ${zone.id}`)
  }

  copyPin(pin: ContextPin): void {
    this.entry = {
      source: 'pin',
      data: { ...pin },
    }
    debug.log('Clipboard', `Copied pin: ${pin.pinType} ${pin.id}`)
  }

  copyMultiple(items: SingleClipboardItem[]): void {
    this.entry = { source: 'multi', data: items }
    debug.log('Clipboard', `Copied ${items.length} items`)
  }

  get(): ClipboardEntry | null {
    return this.entry
  }

  isEmpty(): boolean {
    return this.entry === null
  }

  /** Register the active tool's copy/paste handlers (called by SelectTool on activate) */
  setHandler(handler: ClipboardHandler | null): void {
    this.handler = handler
  }

  /** Called by top bar copy button */
  requestCopy(): void {
    this.handler?.copy()
  }

  /** Called by top bar paste button */
  requestPaste(): void {
    this.handler?.paste()
  }
}

export const clipboard = new ClipboardManager()
